// Hero und Info-Karten der Sonstige-Detailseite (Handoff Sonstige Kontakte §2, Screen 05).
import type { CSSProperties, ReactNode } from 'react';
import { Buildings, EnvelopeSimple, PencilSimple, Phone, Trash, User, type Icon } from '@phosphor-icons/react';
import TelefonLink from '../../../../components/ui/TelefonLink';
import { avatarFarbe } from '../../../../lib/kontakte/sonstige';
import type { SonstigerKontaktKopf, SonstigerKontaktZeile } from '../../../../lib/kontakte/useSonstigerKontakt';
import { KontaktAvatar } from './teile';

const kuerzen: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/** Status wie bisher in KontaktDetail: „Aktiv“ grün, sonst der Status (erster Buchstabe groß); ohne Status kein Chip. */
function statusInfo(status: string | null | undefined): { label: string; aktiv: boolean } | null {
  const roh = (status ?? '').toString().toLowerCase();
  if (!roh) return null;
  const text = String(status);
  return { label: roh === 'aktiv' ? 'Aktiv' : text.charAt(0).toUpperCase() + text.slice(1), aktiv: roh === 'aktiv' };
}

export function DetailHero({ id, kopf, zeile, onBearbeiten, onLoeschen }: {
  id: string;
  kopf: SonstigerKontaktKopf;
  zeile: SonstigerKontaktZeile;
  onBearbeiten: () => void;
  onLoeschen: () => void;
}) {
  const f = avatarFarbe(id);
  const st = statusInfo(zeile.status);
  const unter = zeile.firma_name ?? null;
  const W = zeile.firma_name ? Buildings : User;
  const knopf: CSSProperties = { height: 38, padding: '0 15px 0 12px', border: 'none', borderRadius: 19, gap: 7, fontSize: 13, fontWeight: 700 };
  return (
    <section aria-label="Kontakt" className="sk-glas relative flex flex-wrap items-center"
      style={{ isolation: 'isolate', overflow: 'hidden', borderRadius: 28, padding: 20, gap: 16 }}>
      <div aria-hidden className="pointer-events-none absolute inset-0"
        style={{ zIndex: -1, background: `radial-gradient(90% 120% at 100% 0%, ${f.schein(0.22)}, transparent 60%), radial-gradient(60% 90% at 0% 100%, var(--vio-soft), transparent 70%)` }} />
      <W aria-hidden weight="duotone" size={200} className="pointer-events-none absolute"
        style={{ right: -20, bottom: -50, zIndex: -1, color: f.farbe, opacity: 0.12, transform: 'rotate(-12deg)' }} />
      <KontaktAvatar id={id} name={kopf.anzeigename} groesse={68} />
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 4 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <h1 className="m-0" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.025em', overflowWrap: 'anywhere' }}>{kopf.anzeigename}</h1>
          {st && (
            <span className="inline-flex items-center" style={{ height: 22, padding: '0 9px', borderRadius: 11, gap: 5, fontSize: 11, fontWeight: 800, background: st.aktiv ? 'var(--ok-soft)' : 'var(--fill3)', color: st.aktiv ? 'var(--ok)' : 'var(--ink2)' }}>
              <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
              {st.label}
            </span>
          )}
        </div>
        {unter && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>{unter}</span>}
      </div>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <button type="button" onClick={onBearbeiten} data-tip="Bearbeiten – Stammdaten, Adresse, Kontakt und Bankdaten ändern"
          className="sk-knopf sk-knopf-fill flex cursor-pointer items-center" style={{ ...knopf, color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <PencilSimple weight="bold" size={15} />Bearbeiten
        </button>
        <button type="button" onClick={onLoeschen} data-tip="Löschen – Kontakt mit Verknüpfungen und Kontakt-Kanälen entfernen"
          className="sk-knopf flex cursor-pointer items-center" style={{ ...knopf, color: 'var(--bad)', background: 'var(--bad-soft)' }}>
          <Trash weight="bold" size={15} />Löschen
        </button>
      </div>
    </section>
  );
}

function InfoKarte({ label, icon: I, farbe, wert, tip, link }: {
  label: string; icon: Icon; farbe: number; wert: string | null; tip: string;
  /** Hülle für klickbare Karten (mailto, Telefon) */
  link?: (inhalt: ReactNode, stil: CSSProperties, klasse: string) => ReactNode;
}) {
  const b = `var(--sk-av-${farbe}-b)`;
  const stil: CSSProperties = { isolation: 'isolate', overflow: 'hidden', borderRadius: 22, padding: 14, gap: 12, minWidth: 0, color: 'var(--ink)', textDecoration: 'none' };
  const inhalt = (
    <>
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ zIndex: -1, background: `radial-gradient(100% 120% at 100% 0%, color-mix(in srgb, ${b} 14%, transparent), transparent 65%)` }} />
      <span aria-hidden className="flex shrink-0 items-center justify-center"
        style={{ width: 40, height: 40, borderRadius: 13, color: 'var(--sk-av-ink)', background: `linear-gradient(155deg, var(--sk-av-${farbe}-a), ${b})`, boxShadow: `inset 0 1.5px 0 rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.16), 0 8px 16px -8px ${b}`, transform: 'rotate(-6deg)' }}>
        <I weight="fill" size={20} style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }} />
      </span>
      <span className="flex min-w-0 flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: wert ? 'var(--ink)' : 'var(--ink3)', ...kuerzen }}>{wert ?? '—'}</span>
      </span>
    </>
  );
  if (wert && link) return <>{link(inhalt, stil, 'sk-glas sk-hebt3 relative flex items-center')}</>;
  return <div data-tip={tip} className="sk-glas relative flex items-center" style={stil}>{inhalt}</div>;
}

/** E-Mail (mailto), Telefon (Wähl-Link), Firma – leer „—“. */
export function InfoKarten({ kopf, zeile }: { kopf: SonstigerKontaktKopf; zeile: SonstigerKontaktZeile }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 }}>
      <InfoKarte label="E-Mail" icon={EnvelopeSimple} farbe={0} wert={kopf.email} tip="E-Mail – Mailprogramm öffnen"
        link={(inhalt, stil, klasse) => <a href={`mailto:${kopf.email}`} data-tip="E-Mail – Mailprogramm öffnen" className={klasse} style={stil}>{inhalt}</a>} />
      <InfoKarte label="Telefon" icon={Phone} farbe={2} wert={kopf.telefon} tip="Telefon – Nummer wählen"
        link={(inhalt, stil, klasse) => (
          <span data-tip="Telefon – Nummer wählen" className={klasse} style={{ ...stil, padding: 0 }}>
            <TelefonLink nummer={kopf.telefon} className="flex w-full min-w-0 items-center text-inherit no-underline">
              <span className="relative flex w-full min-w-0 items-center" style={{ padding: stil.padding, gap: stil.gap }}>{inhalt}</span>
            </TelefonLink>
          </span>
        )} />
      <InfoKarte label="Firma" icon={Buildings} farbe={1} wert={zeile.firma_name} tip="Firma – Laut Stammdaten" />
    </div>
  );
}
